// @ts-check
// Grade de 12 colunas (doc 16, seção 7): CSS grid para leitura e, no modo de edição, arrastar pelo grip e
// redimensionar pela alça do canto com encaixe nas células (pointer events: mouse, caneta e toque). Quem colide
// é empurrado para baixo. Soltar o widget sobre uma aba da barra de abas move o widget para ela. Abaixo de 768 px
// vira uma coluna na ordem de linha e coluna, sem arrastar.
import { html } from 'htm/preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useIsMobile } from '../../ui/hooks.js';
import {
  GRID_COLUMN_COUNT,
  GRID_GAP,
  GRID_ROW_HEIGHT,
  MINIMUM_WIDGET_SIZES,
  clampRectangle,
  getBottomRow,
  gridSteps,
  placeItem,
  readGridGap,
  sortByReadingOrder,
} from '../../lib/dashboard-grid.js';
import { WidgetCard } from './widget-card.js';
import { getKpiMetric } from './widget-format.js';
import { isReportWidgetType } from './report-widgets.js';
import { findTabDropTarget } from './dashboard-tab-bar.js';

/**
 * @typedef {import('./dashboard-state.js').DraftWidget} DraftWidget
 * @typedef {import('../../lib/dashboard-grid.js').GridRectangle} GridRectangle
 * @typedef {{ key: string, mode: 'move' | 'resize', startX: number, startY: number, origin: GridRectangle, preview: GridRectangle,
 *   offsetX: number, offsetY: number }} GridInteraction
 */

/** Linhas livres desenhadas abaixo do conteúdo em edição, para haver onde soltar. */
const EDIT_BUFFER_ROWS = 6;
const AUTO_SCROLL_EDGE = 48;
const AUTO_SCROLL_STEP = 14;

/**
 * Cartão que cresce com o conteúdo no celular: os relatórios em cartão e as tabelas no estilo de relatório (a
 * rematrícula por turma); gráficos precisam de altura fixa.
 * @param {DraftWidget} item
 */
const growsWithContent = (item) => isReportWidgetType(item.type) || (item.type === 'KPI' && getKpiMetric(item.configuration?.metric)?.kind === 'TABLE');

/**
 * No celular cada widget ocupa a largura toda. Os cartões de relatório crescem com o conteúdo (sem rolagem dentro
 * da rolagem da página); gráficos precisam de altura fixa.
 * @param {DraftWidget} item
 */
function mobileItemStyle(item) {
  const height = item.rowSpan * GRID_ROW_HEIGHT + (item.rowSpan - 1) * GRID_GAP;
  return growsWithContent(item) ? { minHeight: `${Math.min(height, 2 * GRID_ROW_HEIGHT + GRID_GAP)}px` } : { height: `${height}px` };
}

/**
 * @param {GridRectangle} rectangle
 */
function toGridArea(rectangle) {
  return {
    gridColumn: `${rectangle.gridColumn + 1} / span ${rectangle.columnSpan}`,
    gridRow: `${rectangle.gridRow + 1} / span ${rectangle.rowSpan}`,
  };
}

/**
 * @param {{ items: DraftWidget[], filters: import('../../data/dashboards.js').DashboardRequestFilters, isEditing: boolean,
 *   selectedKey?: string | null, onLayoutChange?: (items: DraftWidget[]) => void, onSelect?: (key: string) => void,
 *   onEdit?: (key: string) => void, onDuplicate?: (key: string) => void, onRemove?: (key: string) => void,
 *   moveTargets?: { key: string, title: string }[], onMoveToTab?: (key: string, tabKey: string) => void, activeTabKey?: string | null,
 *   dashboardId?: string | null }} props
 */
export function DashboardGrid({ items, filters, isEditing, selectedKey, onLayoutChange, onSelect, onEdit, onDuplicate, onRemove, moveTargets = [], onMoveToTab,
  activeTabKey = null, dashboardId = null }) {
  const isMobile = useIsMobile();
  const gridRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [interaction, setInteraction] = useState(/** @type {GridInteraction | null} */ (null));
  const interactionRef = useRef(/** @type {GridInteraction | null} */ (null));
  interactionRef.current = interaction;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const lastPointerRef = useRef(/** @type {{ x: number, y: number } | null} */ (null));
  const hoveredTabRef = useRef(/** @type {HTMLElement | null} */ (null));
  const canArrange = isEditing && !isMobile;

  const previewItems = interaction ? placeItem(items, interaction.key, interaction.preview) : items;

  useEffect(() => {
    if (!interaction) return undefined;
    const grid = gridRef.current;
    const scrollContainer = /** @type {HTMLElement | null} */ (grid?.closest('.dashboard-scroll'));
    /** @param {PointerEvent} event */
    const handleMove = (event) => {
      const current = interactionRef.current;
      if (!current || !grid) return;
      event.preventDefault();
      lastPointerRef.current = { x: event.clientX, y: event.clientY };
      if (current.mode === 'move' && onMoveToTab) {
        const tabKey = findTabDropTarget(event.clientX, event.clientY);
        const element = tabKey && tabKey !== activeTabKey
          ? /** @type {HTMLElement | null} */ (document.querySelector(`[data-dashboard-tab-drop="${CSS.escape(tabKey)}"]`))
          : null;
        if (hoveredTabRef.current !== element) {
          hoveredTabRef.current?.removeAttribute('data-drop-hover');
          element?.setAttribute('data-drop-hover', 'true');
          hoveredTabRef.current = element;
        }
      }
      const { stepX, stepY } = gridSteps(grid.clientWidth, readGridGap(grid));
      const scrollOffset = (scrollContainer?.scrollTop ?? 0) - current.offsetY;
      const deltaX = event.clientX - current.startX;
      const deltaY = event.clientY - current.startY + scrollOffset;
      const widget = itemsRef.current.find((item) => item.key === current.key);
      const minimum = widget ? MINIMUM_WIDGET_SIZES[widget.type] ?? { columnSpan: 1, rowSpan: 1 } : { columnSpan: 1, rowSpan: 1 };
      const preview = current.mode === 'move'
        ? clampRectangle({ ...current.origin, gridColumn: current.origin.gridColumn + deltaX / stepX, gridRow: current.origin.gridRow + deltaY / stepY }, minimum)
        : clampRectangle({
            ...current.origin,
            columnSpan: Math.min(GRID_COLUMN_COUNT - current.origin.gridColumn, current.origin.columnSpan + deltaX / stepX),
            rowSpan: current.origin.rowSpan + deltaY / stepY,
          }, minimum);
      if (current.mode === 'resize') preview.gridColumn = current.origin.gridColumn;
      const isSame = preview.gridColumn === current.preview.gridColumn && preview.gridRow === current.preview.gridRow
        && preview.columnSpan === current.preview.columnSpan && preview.rowSpan === current.preview.rowSpan;
      setInteraction({ ...current, preview: isSame ? current.preview : preview, startX: current.startX, startY: current.startY });
      if (scrollContainer) {
        const bounds = scrollContainer.getBoundingClientRect();
        if (event.clientY > bounds.bottom - AUTO_SCROLL_EDGE) scrollContainer.scrollTop += AUTO_SCROLL_STEP;
        else if (event.clientY < bounds.top + AUTO_SCROLL_EDGE) scrollContainer.scrollTop -= AUTO_SCROLL_STEP;
      }
      // guarda o deslocamento visual do item arrastado para segui-lo suavemente
      if (current.mode === 'move') {
        const element = /** @type {HTMLElement | null} */ (grid.querySelector(`[data-grid-key="${CSS.escape(current.key)}"]`));
        if (element) element.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      }
    };
    const finish = (/** @type {boolean} */ shouldCommit) => {
      const current = interactionRef.current;
      if (grid && current) {
        const element = /** @type {HTMLElement | null} */ (grid.querySelector(`[data-grid-key="${CSS.escape(current.key)}"]`));
        if (element) element.style.transform = '';
      }
      hoveredTabRef.current?.removeAttribute('data-drop-hover');
      hoveredTabRef.current = null;
      const pointer = lastPointerRef.current;
      lastPointerRef.current = null;
      const dropTabKey = current?.mode === 'move' && pointer && onMoveToTab ? findTabDropTarget(pointer.x, pointer.y) : null;
      if (current && shouldCommit && dropTabKey && dropTabKey !== activeTabKey) onMoveToTab?.(current.key, dropTabKey);
      else if (current && shouldCommit) onLayoutChange?.(placeItem(itemsRef.current, current.key, current.preview));
      setInteraction(null);
    };
    const handleUp = () => finish(true);
    const handleCancel = () => finish(false);
    /** @param {KeyboardEvent} event */
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleCancel();
    };
    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [interaction?.key, interaction?.mode]);

  /**
   * @param {DraftWidget} item
   * @param {'move' | 'resize'} mode
   * @param {PointerEvent} event
   */
  const startInteraction = (item, mode, event) => {
    if (!canArrange || event.button > 0) return;
    event.preventDefault();
    event.stopPropagation();
    const scrollContainer = /** @type {HTMLElement | null} */ (gridRef.current?.closest('.dashboard-scroll'));
    const origin = { gridRow: item.gridRow, gridColumn: item.gridColumn, rowSpan: item.rowSpan, columnSpan: item.columnSpan };
    // sem abrir o painel lateral: ele estreitaria a grade no meio do arrasto
    setInteraction({ key: item.key, mode, startX: event.clientX, startY: event.clientY, origin, preview: origin, offsetX: 0, offsetY: scrollContainer?.scrollTop ?? 0 });
  };

  const orderedItems = isMobile ? sortByReadingOrder(previewItems) : previewItems;
  const bottomRow = getBottomRow(previewItems);
  const rowCount = Math.max(1, bottomRow + (isEditing && !isMobile ? EDIT_BUFFER_ROWS : 0));
  const placeholder = interaction ? interaction.preview : null;
  const originalByKey = new Map(items.map((item) => [item.key, item]));

  return html`<div
    ref=${gridRef}
    class="dashboard-grid"
    data-editing=${canArrange ? 'true' : undefined}
    data-interacting=${interaction ? interaction.mode : undefined}
    style=${isMobile ? undefined : { gridTemplateRows: `repeat(${rowCount}, ${GRID_ROW_HEIGHT}px)` }}
  >
    ${canArrange
      ? Array.from({ length: rowCount * GRID_COLUMN_COUNT }, (_, index) => html`<span
          key=${`cell-${index}`}
          class="dashboard-grid-cell"
          aria-hidden="true"
          style=${{ gridColumn: `${(index % GRID_COLUMN_COUNT) + 1}`, gridRow: `${Math.floor(index / GRID_COLUMN_COUNT) + 1}` }}
        ></span>`)
      : null}
    ${placeholder ? html`<div class="dashboard-grid-placeholder" style=${toGridArea(placeholder)} aria-hidden="true"></div>` : null}
    ${orderedItems.map((item) => {
      const isDragged = interaction?.key === item.key;
      // o item arrastado fica na posição de origem (com translate); o redimensionado já mostra o tamanho novo
      const rectangle = isDragged && interaction?.mode === 'move' ? /** @type {DraftWidget} */ (originalByKey.get(item.key) ?? item) : item;
      return html`<div
        key=${item.key}
        class="dashboard-grid-item"
        data-grid-key=${item.key}
        data-dragging=${isDragged ? interaction?.mode : undefined}
        data-auto-height=${isMobile && growsWithContent(item) ? 'true' : undefined}
        style=${isMobile ? mobileItemStyle(item) : toGridArea(rectangle)}
      >
        <${WidgetCard}
          widget=${item}
          filters=${filters}
          dashboardId=${dashboardId}
          rowSpan=${rectangle.rowSpan}
          isEditing=${isEditing}
          isSelected=${selectedKey === item.key}
          onSelect=${() => onSelect?.(item.key)}
          onEdit=${() => onEdit?.(item.key)}
          onDuplicate=${() => onDuplicate?.(item.key)}
          onRemove=${() => onRemove?.(item.key)}
          moveTargets=${moveTargets}
          onMoveToTab=${onMoveToTab ? (/** @type {string} */ tabKey) => onMoveToTab(item.key, tabKey) : undefined}
          onGripPointerDown=${canArrange ? (/** @type {PointerEvent} */ event) => startInteraction(item, 'move', event) : undefined}
          onResizePointerDown=${canArrange ? (/** @type {PointerEvent} */ event) => startInteraction(item, 'resize', event) : undefined}
        />
      </div>`;
    })}
  </div>`;
}
